import { useMemo } from 'react';
import { create } from 'zustand';
import { http } from '@/libs/http';
import { useGroups } from '@/store/group';
import { useIdps } from '@/store/idp';
import { toast } from 'sonner';
import type { Idp, Tenant, TenantMemberListResponse, TenantStats, TenantSettings, TenantDomain } from '@/types';

type TenantScopedState = {
    users: any[];
    groups: any[];
    availableGroups: any[];
    idps: any[];
    availableIdps: Idp[];
    apps: string[];
    domains: TenantDomain[];
    isLoadingUsers: boolean;
    isLoadingGroups: boolean;
    isLoadingIdps: boolean;
    isLoadingApps: boolean;
    isLoadingDomains: boolean;
    members?: TenantMemberListResponse | null;
    stats?: TenantStats | null;
    isLoadingMembers: boolean;
    isLoadingStats: boolean;
};

type TenantStoreState = {
    tenants: Tenant[];
    tenantScoped: Record<string, TenantScopedState>;
    isLoading: boolean;
    error: string | null;
    list: (filter?: (tenants: Tenant[]) => Tenant[]) => Promise<Tenant[]>;
    get: (id: string) => Promise<Tenant>;
    reorder: (orderedIds: string[]) => Promise<void>;
    create: (data: Partial<Tenant> & { owner_email?: string }) => Promise<Tenant>;
    update: (id: string, data: Partial<Tenant>) => Promise<Tenant>;
    delete: (id: string, mode?: "soft" | "hard", confirmSlug?: string) => Promise<void>;
    select: (criteria: (tenants: Tenant[]) => Tenant) => Tenant;
    listUsers: (tenantId: string) => Promise<any[]>;
    addUser: (tenantId: string, userId: string) => Promise<void>;
    removeUser: (tenantId: string, userId: string) => Promise<void>;
    listGroups: (tenantId: string) => Promise<any[]>;
    addGroup: (tenantId: string, groupId: string) => Promise<void>;
    removeGroup: (tenantId: string, groupId: string) => Promise<void>;
    listAvailableGroups: (tenantId: string) => Promise<any[]>;
    listIdps: (tenantId: string) => Promise<any[]>;
    listAvailableIdps: (tenantId: string) => Promise<Idp[]>;
    addIdp: (tenantId: string, idpId: string, config?: any) => Promise<void>;
    updateIdp: (tenantId: string, idpId: string, config?: any) => Promise<void>;
    removeIdp: (tenantId: string, idpId: string) => Promise<void>;

    listApps: (tenantId: string) => Promise<string[]>;
    enableApp: (tenantId: string, appId: string) => Promise<void>;
    disableApp: (tenantId: string, appId: string) => Promise<void>;
    listDomains: (tenantId: string) => Promise<TenantDomain[]>;
    addDomain: (tenantId: string, domain: string, isVerified?: boolean) => Promise<void>;
    updateDomain: (tenantId: string, domainId: string, data: { domain: string, is_verified?: boolean }) => Promise<void>;
    removeDomain: (tenantId: string, domainId: string) => Promise<void>;
    getMembers: (tenantId: string, page?: number, pageSize?: number, status?: number, search?: string) => Promise<TenantMemberListResponse>;
    getStats: (tenantId: string) => Promise<TenantStats>;
    updateSettings: (tenantId: string, settings: Partial<TenantSettings>) => Promise<Tenant>;
    deactivate: (tenantId: string, reason?: string) => Promise<void>;
    reactivate: (tenantId: string) => Promise<void>;
    hardDelete: (tenantId: string, confirmSlug: string) => Promise<void>;
    getPgpPublicKey: (tenantId: string) => Promise<string>;
    generatePgpKeypair: (tenantId: string) => Promise<Tenant>;
    decryptPgpMessage: (tenantId: string, message: string) => Promise<string>;
    decryptPgpFile: (tenantId: string, file: File) => Promise<Blob>;
};

const toArray = <T>(payload: unknown): T[] => {
    if (Array.isArray(payload)) return payload as T[];
    const data = payload as any;
    if (!data || typeof data !== 'object') return [];

    if (Array.isArray(data.items)) return data.items as T[];
    if (Array.isArray(data.data)) return data.data as T[];
    if (Array.isArray(data.results)) return data.results as T[];
    if (Array.isArray(data.tenants)) return data.tenants as T[];
    if (Array.isArray(data.users)) return data.users as T[];
    if (Array.isArray(data.groups)) return data.groups as T[];
    if (Array.isArray(data.idps)) return data.idps as T[];
    if (Array.isArray(data.apps)) return data.apps as T[];
    if (Array.isArray(data.domains)) return data.domains as T[];

    if (data.data && typeof data.data === 'object') {
        const nested = toArray<T>(data.data);
        if (nested.length > 0) return nested;
    }

    // Fallback: if response object contains any array field, use the first one.
    const firstArray = Object.values(data).find((value) => Array.isArray(value));
    if (Array.isArray(firstArray)) return firstArray as T[];

    return [];
};

const toTenant = (payload: unknown): Tenant => {
    const data = payload as any;
    return (data?.tenant ?? data?.data ?? data) as Tenant;
};

const EMPTY_SCOPED_STATE: TenantScopedState = {
    users: [],
    groups: [],
    availableGroups: [],
    idps: [],
    availableIdps: [],
    apps: [],
    domains: [],
    isLoadingUsers: false,
    isLoadingGroups: false,
    isLoadingIdps: false,
    isLoadingApps: false,
    isLoadingDomains: false,
    members: null,
    stats: null,
    isLoadingMembers: false,
    isLoadingStats: false,
};

const emptyScopedState = (): TenantScopedState => ({ ...EMPTY_SCOPED_STATE });

const scopedFor = (state: TenantStoreState, tenantId: string): TenantScopedState =>
    state.tenantScoped[tenantId] ?? emptyScopedState();

export const useTenants = create<TenantStoreState>((set, get) => ({
    tenants: [],
    tenantScoped: {},
    isLoading: false,
    error: null,

    list: async (filter) => {
        set({ isLoading: true, error: null });
        try {
            const payload = await http.get('/mgmt/v1/tenants');
            const data = toArray<Tenant>(payload);
            set({ tenants: data, isLoading: false });
            return filter ? filter(data) : data;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to load tenants';
            set({ error: errorMsg, isLoading: false });
            return [];
        }
    },

    reorder: async (orderedIds) => {
        const previous = get().tenants;
        const byId = new Map(previous.map((t) => [t.id, t]));
        const reordered = orderedIds.map((id) => byId.get(id)).filter((t): t is Tenant => !!t);
        set({ tenants: reordered });
        try {
            await http.put('/mgmt/v1/tenants/order', { tenant_ids: orderedIds });
        } catch (err: any) {
            set({ tenants: previous });
            toast.error(err.message || 'Failed to save tenant order');
        }
    },

    get: async (id) => {
        const existing = get().tenants.find((tenant) => tenant.id === id);
        if (existing) return existing;
        const payload = await http.get(`/mgmt/v1/tenants/${id}`);
        const tenant = toTenant(payload);
        set({ tenants: [...get().tenants.filter((item) => item.id !== id), tenant] });
        return tenant;
    },

    create: async (data) => {
        const previousTenants = get().tenants;
        const tempId = `temp-${Date.now()}`;
        const tempTenant = {
            ...data,
            id: tempId,
            name: data.name || 'New Tenant',
            slug: data.slug || 'new-tenant',
        } as Tenant;

        set({ tenants: [...previousTenants, tempTenant] });

        try {
            const payload = await http.post('/mgmt/v1/tenants', data);
            const created = toTenant(payload);
            set({
                tenants: get().tenants.map((tenant) => (tenant.id === tempId ? created : tenant)),
            });
            toast.success('Tenant created successfully');
            return created;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to create tenant';
            set({ tenants: previousTenants, error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    update: async (id, data) => {
        const previousTenants = get().tenants;
        set({
            tenants: previousTenants.map((tenant) =>
                tenant.id === id ? { ...tenant, ...data } : tenant,
            ),
        });

        try {
            const payload = await http.put(`/mgmt/v1/tenants/${id}`, data);
            const updated = toTenant(payload);
            set({
                tenants: get().tenants.map((tenant) => (tenant.id === id ? updated : tenant)),
            });
            return updated;
        } catch (err: any) {
            set({ tenants: previousTenants, error: err.message });
            throw err;
        }
    },

    delete: async (id, mode = "soft", confirmSlug) => {
        try {
            await http.delete(`/mgmt/v1/tenants/${id}`, {
                params: { mode, confirm_slug: confirmSlug }
            });
            set((state) => ({
                tenants: state.tenants.filter((t) => t.id !== id)
            }));
        } catch (err: any) {
            set({ error: err.message });
            throw err;
        }
    },

    select: (criteria) => criteria(get().tenants),

    listUsers: async (tenantId) => {
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    isLoadingUsers: true,
                },
            },
        }));
        try {
            const payload = await http.get(`/mgmt/v1/tenants/${tenantId}/users`);
            const users = toArray<any>(payload);
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        users,
                        isLoadingUsers: false,
                    },
                },
            }));
            return users;
        } catch (err: any) {
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        isLoadingUsers: false,
                    },
                },
            }));
            throw err;
        }
    },
    addUser: async (tenantId, userId) => {
        await http.post(`/mgmt/v1/tenants/${tenantId}/users/${userId}`, {});
        await get().listUsers(tenantId);
    },
    removeUser: async (tenantId, userId) => {
        await http.delete(`/mgmt/v1/tenants/${tenantId}/users/${userId}`);
        await get().listUsers(tenantId);
    },

    listGroups: async (tenantId) => {
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    isLoadingGroups: true,
                },
            },
        }));
        try {
            const payload = await http.get(`/mgmt/v1/tenants/${tenantId}/groups`);
            const groups = toArray<any>(payload);
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        groups,
                        isLoadingGroups: false,
                    },
                },
            }));
            return groups;
        } catch (err: any) {
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        isLoadingGroups: false,
                    },
                },
            }));
            throw err;
        }
    },
    addGroup: async (tenantId, groupId) => {
        await http.post(`/mgmt/v1/tenants/${tenantId}/groups/${groupId}`, {});
        await get().listGroups(tenantId);
    },
    removeGroup: async (tenantId, groupId) => {
        await http.delete(`/mgmt/v1/tenants/${tenantId}/groups/${groupId}`);
        await get().listGroups(tenantId);
    },
    listAvailableGroups: async (tenantId) => {
        const groups = toArray<any>(await useGroups.getState().list());
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    availableGroups: groups,
                },
            },
        }));
        return groups;
    },

    listIdps: async (tenantId) => {
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    isLoadingIdps: true,
                },
            },
        }));
        try {
            const payload = await http.get(`/mgmt/v1/tenants/${tenantId}/idps`);
            const idps = toArray<any>(payload);
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        idps,
                        isLoadingIdps: false,
                    },
                },
            }));
            return idps;
        } catch (err: any) {
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        isLoadingIdps: false,
                    },
                },
            }));
            throw err;
        }
    },
    addIdp: async (tenantId, idpId, config = {}) => {
        await http.post(`/mgmt/v1/tenants/${tenantId}/idps`, { idp_id: idpId, ...config });
        await get().listIdps(tenantId);
    },
    updateIdp: async (tenantId, idpId, config = {}) => {
        await http.put(`/mgmt/v1/tenants/${tenantId}/idps/${idpId}`, config);
        await get().listIdps(tenantId);
    },
    removeIdp: async (tenantId, idpId) => {
        await http.delete(`/mgmt/v1/tenants/${tenantId}/idps/${idpId}`);
        await get().listIdps(tenantId);
    },
    listAvailableIdps: async (tenantId) => {
        const idps = toArray<Idp>(await useIdps.getState().list());
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    availableIdps: idps,
                },
            },
        }));
        return idps;
    },

    listApps: async (tenantId) => {
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    isLoadingApps: true,
                },
            },
        }));
        try {
            const payload = await http.get(`/mgmt/v1/tenants/${tenantId}/apps`);
            const apps = toArray<any>(payload).map(item => typeof item === 'string' ? item : item.application_id);
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        apps,
                        isLoadingApps: false,
                    },
                },
            }));
            return apps;
        } catch (err: any) {
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        isLoadingApps: false,
                    },
                },
            }));
            throw err;
        }
    },
    enableApp: async (tenantId, appId) => {
        await http.post(`/mgmt/v1/tenants/${tenantId}/apps/${appId}`, {});
        await get().listApps(tenantId);
    },
    disableApp: async (tenantId, appId) => {
        await http.delete(`/mgmt/v1/tenants/${tenantId}/apps/${appId}`);
        await get().listApps(tenantId);
    },

    listDomains: async (tenantId) => {
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    isLoadingDomains: true,
                },
            },
        }));
        try {
            const payload = await http.get(`/mgmt/v1/tenants/${tenantId}/domains`);
            const domains = toArray<TenantDomain>(payload);
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        domains,
                        isLoadingDomains: false,
                    },
                },
            }));
            return domains;
        } catch (err: any) {
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        isLoadingDomains: false,
                    },
                },
            }));
            throw err;
        }
    },
    addDomain: async (tenantId, domain, isVerified = false) => {
        await http.post(`/mgmt/v1/tenants/${tenantId}/domains`, { domain, is_verified: isVerified });
        await get().listDomains(tenantId);
    },
    updateDomain: async (tenantId, domainId, data) => {
        await http.put(`/mgmt/v1/tenants/${tenantId}/domains/${domainId}`, data);
        await get().listDomains(tenantId);
    },
    removeDomain: async (tenantId, domainId) => {
        await http.delete(`/mgmt/v1/tenants/${tenantId}/domains/${domainId}`);
        await get().listDomains(tenantId);
    },

    getMembers: async (tenantId, page = 1, pageSize = 20, status?, search?) => {
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    isLoadingMembers: true,
                },
            },
        }));
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: pageSize.toString(),
                ...(status !== undefined && { status: status.toString() }),
                ...(search && { search }),
            });
            const payload = await http.get(`/mgmt/v1/tenants/${tenantId}/members?${params}`);
            const members = payload as TenantMemberListResponse;
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        members,
                        isLoadingMembers: false,
                    },
                },
            }));
            return members;
        } catch (err: any) {
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        isLoadingMembers: false,
                    },
                },
            }));
            throw err;
        }
    },

    getStats: async (tenantId) => {
        set((state) => ({
            tenantScoped: {
                ...state.tenantScoped,
                [tenantId]: {
                    ...scopedFor(state, tenantId),
                    isLoadingStats: true,
                },
            },
        }));
        try {
            const payload = await http.get(`/mgmt/v1/tenants/${tenantId}/stats`);
            const stats = payload as TenantStats;
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        stats,
                        isLoadingStats: false,
                    },
                },
            }));
            return stats;
        } catch (err: any) {
            set((state) => ({
                tenantScoped: {
                    ...state.tenantScoped,
                    [tenantId]: {
                        ...scopedFor(state, tenantId),
                        isLoadingStats: false,
                    },
                },
            }));
            throw err;
        }
    },

    updateSettings: async (tenantId, settings) => {
        try {
            const payload = await http.put(`/mgmt/v1/tenants/${tenantId}/settings`, settings);
            const updated = toTenant(payload);
            set({
                tenants: get().tenants.map((tenant) => (tenant.id === tenantId ? updated : tenant)),
            });
            toast.success('Tenant settings updated successfully');
            return updated;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to update tenant settings';
            toast.error(errorMsg);
            throw err;
        }
    },

    deactivate: async (tenantId, reason?) => {
        const previousTenants = get().tenants;
        set({
            tenants: previousTenants.map((tenant) =>
                tenant.id === tenantId ? { ...tenant, status: 2 } : tenant
            ),
        });

        try {
            await http.post(`/mgmt/v1/tenants/${tenantId}/deactivate`, { reason });
            toast.success('Tenant deactivated successfully');
            await get().get(tenantId); // Refresh tenant data
        } catch (err: any) {
            set({ tenants: previousTenants });
            const errorMsg = err.message || 'Failed to deactivate tenant';
            toast.error(errorMsg);
            throw err;
        }
    },

    reactivate: async (tenantId) => {
        const previousTenants = get().tenants;
        set({
            tenants: previousTenants.map((tenant) =>
                tenant.id === tenantId ? { ...tenant, status: 1 } : tenant
            ),
        });

        try {
            await http.post(`/mgmt/v1/tenants/${tenantId}/reactivate`, {});
            toast.success('Tenant reactivated successfully');
            await get().get(tenantId); // Refresh tenant data
        } catch (err: any) {
            set({ tenants: previousTenants });
            const errorMsg = err.message || 'Failed to reactivate tenant';
            toast.error(errorMsg);
            throw err;
        }
    },
    hardDelete: async (tenantId, confirmSlug) => {
        const previousTenants = get().tenants;
        set({ tenants: previousTenants.filter((tenant) => tenant.id !== tenantId) });

        try {
            await http.delete(`/mgmt/v1/tenants/${tenantId}`, {
                params: { mode: 'hard', confirm_slug: confirmSlug }
            });
            toast.success('Tenant permanently deleted');
        } catch (err: any) {
            set({ tenants: previousTenants });
            const errorMsg = err.message || 'Failed to permanently delete tenant';
            toast.error(errorMsg);
            throw err;
        }
    },

    getPgpPublicKey: async (tenantId) => {
        try {
            const res = await http.get<{ pgp_public_key: string }>(`/mgmt/v1/tenants/${tenantId}/pgp-key`);
            return res.pgp_public_key || '';
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to fetch PGP public key';
            toast.error(errorMsg);
            throw err;
        }
    },

    generatePgpKeypair: async (tenantId) => {
        try {
            const payload = await http.post(`/mgmt/v1/tenants/${tenantId}/pgp-key/generate`);
            const updated = toTenant(payload);
            set({
                tenants: get().tenants.map((tenant) => (tenant.id === tenantId ? updated : tenant)),
            });
            toast.success('PGP keypair regenerated successfully');
            return updated;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to generate PGP keypair';
            toast.error(errorMsg);
            throw err;
        }
    },
    decryptPgpMessage: async (tenantId, message) => {
        try {
            const res = await http.post<{ decrypted: string }>(`/mgmt/v1/tenants/${tenantId}/pgp-key/decrypt`, { message });
            return res.decrypted || '';
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to decrypt PGP message';
            toast.error(errorMsg);
            throw err;
        }
    },
    decryptPgpFile: async (tenantId, file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await http.post<Blob>(
                `/mgmt/v1/tenants/${tenantId}/pgp-key/decrypt-file`,
                formData,
                {
                    responseType: 'blob',
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return res;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to decrypt PGP file';
            toast.error(errorMsg);
            throw err;
        }
    },

    /**
     * Reset store to initial state (for tenant context switching)
     */
    reset: () => {
        set({
            tenants: [],
            tenantScoped: {},
            isLoading: false,
            error: null,
        });
    },
}));

export const useTenant = (id?: string) => {
    const tenant = useTenants((state) =>
        id ? state.tenants.find((item) => item.id === id) ?? null : null,
    );
    const scoped = useTenants((state) =>
        id ? (state.tenantScoped[id] ?? EMPTY_SCOPED_STATE) : EMPTY_SCOPED_STATE,
    );

    return useMemo(() => {
        const ensureId = (): string => {
            if (!id) throw new Error('Tenant id is required.');
            return id;
        };

        return {
            tenant,
            get: () => useTenants.getState().get(ensureId()),
            update: (data: Partial<Tenant>) => useTenants.getState().update(ensureId(), data),
            delete: (mode?: "soft" | "hard", confirmSlug?: string) => useTenants.getState().delete(ensureId(), mode, confirmSlug),
            hardDelete: (confirmSlug: string) => useTenants.getState().hardDelete(ensureId(), confirmSlug),
            getPgpPublicKey: () => useTenants.getState().getPgpPublicKey(ensureId()),
            generatePgpKeypair: () => useTenants.getState().generatePgpKeypair(ensureId()),
            decryptPgpMessage: (message: string) => useTenants.getState().decryptPgpMessage(ensureId(), message),
            decryptPgpFile: (file: File) => useTenants.getState().decryptPgpFile(ensureId(), file),
            users: {
                data: scoped.users,
                isLoading: scoped.isLoadingUsers,
                list: () => useTenants.getState().listUsers(ensureId()),
                create: (userId: string) => useTenants.getState().addUser(ensureId(), userId),
                delete: (userId: string) => useTenants.getState().removeUser(ensureId(), userId),
            },
            groups: {
                data: scoped.groups,
                available: scoped.availableGroups,
                isLoading: scoped.isLoadingGroups,
                list: () => useTenants.getState().listGroups(ensureId()),
                create: (groupId: string) => useTenants.getState().addGroup(ensureId(), groupId),
                delete: (groupId: string) => useTenants.getState().removeGroup(ensureId(), groupId),
                listAvailable: () => useTenants.getState().listAvailableGroups(ensureId()),
            },
            idps: {
                data: scoped.idps,
                available: scoped.availableIdps,
                isLoading: scoped.isLoadingIdps,
                list: () => useTenants.getState().listIdps(ensureId()),
                assign: (idpId: string, config?: any) => useTenants.getState().addIdp(ensureId(), idpId, config),
                update: (idpId: string, config?: any) => useTenants.getState().updateIdp(ensureId(), idpId, config),
                remove: (idpId: string) => useTenants.getState().removeIdp(ensureId(), idpId),
                listAvailable: () => useTenants.getState().listAvailableIdps(ensureId()),
            },
            apps: {
                data: scoped.apps,
                isLoading: scoped.isLoadingApps,
                list: () => useTenants.getState().listApps(ensureId()),
                enable: (appId: string) => useTenants.getState().enableApp(ensureId(), appId),
                disable: (appId: string) => useTenants.getState().disableApp(ensureId(), appId),
            },
            domains: {
                data: scoped.domains,
                isLoading: scoped.isLoadingDomains,
                list: () => useTenants.getState().listDomains(ensureId()),
                create: (domain: string, isVerified?: boolean) => useTenants.getState().addDomain(ensureId(), domain, isVerified),
                update: (domainId: string, data: { domain: string, is_verified?: boolean }) => useTenants.getState().updateDomain(ensureId(), domainId, data),
                delete: (domainId: string) => useTenants.getState().removeDomain(ensureId(), domainId),
            },
        };
    }, [id, tenant, scoped]);
};
